import { lazy, StrictMode, Suspense, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AuthProvider, isStaff, useAuth } from './auth.tsx';
import { Layout } from './components/Layout.tsx';
import { Loading } from './components/ui.tsx';
import { AccountPage } from './pages/Account.tsx';
import { AccessibilityPage, PrivacyPage } from './pages/About.tsx';
import { CertificatesPage, VerifyCertificatePage } from './pages/Certificates.tsx';
import { CoursePage } from './pages/Course.tsx';
import { HomePage } from './pages/Home.tsx';
import { ItemPage } from './pages/Item.tsx';
import {
  ConfirmEmailChangePage,
  ForgotPasswordPage,
  LinkLoginPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  UnsubscribePage,
  VerifyEmailPage,
} from './pages/Auth.tsx';
import { JournalPage, ReadingListPage } from './pages/Journal.tsx';
import { NotFoundPage } from './pages/NotFound.tsx';
import { NorrbottenPage } from './pages/Norrbotten.tsx';
import { AboutAuroraPage, ContactPage } from './pages/AboutAurora.tsx';
import { JoinPage } from './pages/Join.tsx';
import './styles.css';

// Lärarsidorna, kommunvyn och OAuth-godkännandet används av få och laddas först när de behövs,
// så att deltagarnas sidor laddar snabbare.
const TeacherRoutes = lazy(() =>
  import('./pages/teacher/TeacherRoutes.tsx').then((m) => ({ default: m.TeacherRoutes })),
);
const MunicipalityPage = lazy(() =>
  import('./pages/Municipality.tsx').then((m) => ({ default: m.MunicipalityPage })),
);
const OAuthAuthorizePage = lazy(() =>
  import('./pages/OAuth.tsx').then((m) => ({ default: m.OAuthAuthorizePage })),
);

function RequireUser({ children, teacher }: { children: ReactNode; teacher?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user)
    return (
      <Navigate to={`/logga-in?till=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  if (!user.emailVerified && !teacher) return <Navigate to="/verifiera-epost" replace />;
  if (teacher && !isStaff(user)) return <Navigate to="/kurs" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="logga-in" element={<LoginPage />} />
          <Route path="registrera" element={<RegisterPage />} />
          <Route path="verifiera-epost" element={<VerifyEmailPage />} />
          <Route path="glomt-losenord" element={<ForgotPasswordPage />} />
          <Route path="logga-in-med-lank" element={<LinkLoginPage />} />
          <Route path="anmal/:token" element={<JoinPage />} />
          <Route path="nytt-losenord" element={<ResetPasswordPage />} />
          <Route path="bekrafta-ny-epost" element={<ConfirmEmailChangePage />} />
          <Route path="verifiera/:code" element={<VerifyCertificatePage />} />
          <Route path="avregistrera" element={<UnsubscribePage />} />
          <Route path="norrbotten" element={<NorrbottenPage />} />
          <Route path="om" element={<AboutAuroraPage />} />
          <Route path="kontakt" element={<ContactPage />} />
          <Route path="integritet" element={<PrivacyPage />} />
          <Route path="tillganglighet" element={<AccessibilityPage />} />
          <Route
            path="kurs"
            element={
              <RequireUser>
                <CoursePage />
              </RequireUser>
            }
          />
          <Route
            path="kurs/modul/:id"
            element={
              <RequireUser>
                <CoursePage />
              </RequireUser>
            }
          />
          <Route
            path="kurs/del/:id"
            element={
              <RequireUser>
                <ItemPage />
              </RequireUser>
            }
          />
          <Route
            path="dagbok"
            element={
              <RequireUser>
                <JournalPage />
              </RequireUser>
            }
          />
          <Route
            path="lasning"
            element={
              <RequireUser>
                <ReadingListPage />
              </RequireUser>
            }
          />
          <Route
            path="intyg"
            element={
              <RequireUser>
                <CertificatesPage />
              </RequireUser>
            }
          />
          <Route
            path="konto"
            element={
              <RequireUser teacher={false}>
                <AccountPage />
              </RequireUser>
            }
          />
          <Route
            path="kommun"
            element={
              <RequireUser teacher={false}>
                <MunicipalityPage />
              </RequireUser>
            }
          />
          <Route
            path="larare/*"
            element={
              <RequireUser teacher>
                <TeacherRoutes />
              </RequireUser>
            }
          />
          <Route
            path="oauth/authorize"
            element={
              <RequireUser teacher={false}>
                <OAuthAuthorizePage />
              </RequireUser>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
